import { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { DocumentNode, NetworkStatus, useQuery } from '@apollo/client'
import { observer } from 'mobx-react'

import { GLOBAL_ROUTE_PATHS } from 'Router/Router.constants'

import { RentalsPreviewColumn } from 'Modules/rentals/components/RentalsPreviewColumn/RentalsPreviewColumn'
import { RentalsTable } from 'Modules/rentals/components/RentalsTable/RentalsTable'

import { Card } from 'Components/_theme'
import { ContentSidebar } from 'Components/ContentSidebar/ContentSidebar'
import { FaqCard } from 'Components/FaqCard/FaqCard'
import { ImpactTracker } from 'Components/ImpactTracker/ImpactTracker'
import { ListSearch } from 'Components/ListSearch/ListSearch'
import { ObserveMeOnceIAmMounted } from 'Components/ObserveMeOnceIAmMounted/ObserveMeOnceIAmMounted'
import { PageErrorState } from 'Components/PageErrorState/PageErrorState'
import { PageHeader } from 'Components/PageHeader/PageHeader'
import { PageLoadingState } from 'Components/PageLoadingState/PageLoadingState'

import { useStore } from 'Stores/index'
import { OriginRouteTypeEnum } from 'Stores/orderStore/orderStore.types'

import { useActiveUser, useIsOrganisation } from 'Hooks'

import { getQueryObject, toggleIntercomVisibility } from 'Utilities'

import { PAGINATION } from 'Constants/constants'
import { E2E_NEW_ORDER_BUTTON } from 'Constants/e2e'
import {
  getEmployeeDevices,
  getEmployeeDevices_employee_rentals,
  getEmployeeDevicesVariables,
} from 'Constants/graphql/queries/listing/__generated__/getEmployeeDevices'
import {
  getOrganisationDevices,
  getOrganisationDevices_organisation_rentals,
  getOrganisationDevicesVariables,
} from 'Constants/graphql/queries/listing/__generated__/getOrganisationDevices'
import {
  GET_EMPLOYEE_DEVICES,
  GET_ORGANISATION_DEVICES,
} from 'Constants/graphql/queries/listing/GetDevices'
import { PortalSectionEnum } from 'Constants/portalSection'

import { composePath } from 'Portal/src/utilities/composePath/composePath.util'

export const Rentals = observer(() => {
  const { portalConfigStore, orderStore, portalSettingsStore } = useStore()
  const { portalConfig } = portalConfigStore
  const { isAppReadOnly } = portalSettingsStore
  const { setOriginRoute } = orderStore

  const { activeEmployee, activeOrganisation } = useActiveUser()
  const isOrganisation = useIsOrganisation()
  const intl = useIntl()

  const [selectedEntityId, setSelectedEntityId] = useState('')
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null)
  const [isPreviewColumnOpen, setIsPreviewColumnOpen] = useState(false)

  const { employeeId, organisationId } = useParams<{
    employeeId?: string
    organisationId?: string
  }>()
  const history = useHistory()
  const { search: locationSearch } = useLocation()
  const parsedSearch = getQueryObject(locationSearch)

  const handleOpenPreviewColumn = useCallback(
    (id: string, rowIndex: number | null) => {
      toggleIntercomVisibility(false)

      setSelectedEntityId(id)
      setSelectedRowIndex(rowIndex)
      setIsPreviewColumnOpen(true)
    },
    [],
  )

  const handleClosePreviewColumn = useCallback(() => {
    toggleIntercomVisibility(true)

    setIsPreviewColumnOpen(false)
    setSelectedRowIndex(null)
  }, [])

  const { newSmartphoneButtonIsVisible, query, queryId } = useMemo((): {
    newSmartphoneButtonIsVisible: boolean
    query: DocumentNode
    queryId: string
  } => {
    if (isOrganisation) {
      return {
        newSmartphoneButtonIsVisible:
          !!portalConfig?.newDevice &&
          !!portalConfig?.showNewDeviceButtonToAdmin &&
          !isAppReadOnly,
        query: GET_ORGANISATION_DEVICES,
        queryId: organisationId!,
      }
    }

    const newSmartphoneButtonIsVisible =
      !!portalConfig?.newDevice &&
      !!activeEmployee?.canOrderNewDevice &&
      !!activeEmployee?.canOrder

    return {
      newSmartphoneButtonIsVisible,
      query: GET_EMPLOYEE_DEVICES,
      queryId: employeeId!,
    }
  }, [
    activeEmployee?.canOrder,
    activeEmployee?.canOrderNewDevice,
    employeeId,
    isAppReadOnly,
    isOrganisation,
    organisationId,
    portalConfig?.newDevice,
    portalConfig?.showNewDeviceButtonToAdmin,
  ])

  const {
    data,
    error,
    fetchMore,
    loading: isLoading,
    networkStatus,
    refetch,
  } = useQuery<
    getEmployeeDevices | getOrganisationDevices,
    getEmployeeDevicesVariables | getOrganisationDevicesVariables
  >(query, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      after: PAGINATION.AFTER,
      first: PAGINATION.ITEMS_PER_PAGE,
      id: queryId,
      searchterm: (parsedSearch?.searchTerm as string) ?? null,
    },
  })

  const handleSearch = useCallback(
    (value: string) => {
      refetch({
        searchterm: value || null,
      })
    },
    [refetch],
  )

  useEffect(() => {
    refetch({ searchterm: (parsedSearch?.searchTerm as string) ?? null })
  }, [refetch, parsedSearch?.searchTerm])

  if (error) {
    return <PageErrorState />
  }

  if (isLoading && !data) return <PageLoadingState />
  if (!data) return <PageErrorState />

  const getRentals = (
    data: getEmployeeDevices | getOrganisationDevices,
  ):
    | getEmployeeDevices_employee_rentals
    | getOrganisationDevices_organisation_rentals
    | null =>
    isOrganisation
      ? (data as getOrganisationDevices).organisation.rentals
      : (data as getEmployeeDevices).employee.rentals

  const onLoadMore = () => {
    if (data)
      fetchMore({
        variables: {
          after: getRentals(data)?.pageInfo.endCursor || PAGINATION.AFTER,
        },
      })
  }

  const intersectionObserver = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) {
      onLoadMore()
    }
  })

  const rentals = getRentals(data)?.rentals
  const totalRentals = getRentals(data)?.totalCount
  const hasNextPage = getRentals(data)?.pageInfo.hasNextPage

  const handleClickNewSmartphoneButton = () => {
    setOriginRoute({
      type: OriginRouteTypeEnum.Rentals,
      url: history.location.pathname,
    })

    const path = composePath({
      isOrganisation,
      params: {
        employeeId: activeEmployee?.id,
        organisationId: activeOrganisation?.id,
      },
      paths: {
        employee: GLOBAL_ROUTE_PATHS.employee.orderNew,
        organisation: GLOBAL_ROUTE_PATHS.organisation.orderNew,
      },
    })

    history.push(path)
  }

  return (
    <Container className="pt-6" fluid>
      <div>
        <PageHeader portalSection={PortalSectionEnum.Rentals} />
      </div>

      <Row>
        <Col xl={12} xxl={8}>
          <Card className="mb-4">
            <ListSearch
              buttonE2ESelector={E2E_NEW_ORDER_BUTTON}
              buttonLabel={intl.formatMessage({ id: 'New_device' })}
              buttonOnClick={handleClickNewSmartphoneButton}
              buttonVisible={newSmartphoneButtonIsVisible}
              handleSearch={handleSearch}
              totalCount={totalRentals ?? 0}
            />
            <Row>
              <Col>
                <RentalsTable
                  isActiveEmployee={!isOrganisation}
                  isLoading={isLoading}
                  isSearching={networkStatus === NetworkStatus.setVariables}
                  onOpenPreviewColumn={handleOpenPreviewColumn}
                  rentals={rentals ?? []}
                  selectedRowIndex={selectedRowIndex}
                />
              </Col>
            </Row>
          </Card>
          <Row className="p-1">
            <Col>
              {hasNextPage && !isLoading && (
                <ObserveMeOnceIAmMounted
                  intersectionObserver={intersectionObserver}
                />
              )}
            </Col>
          </Row>
        </Col>

        <ContentSidebar
          isPreviewColumnShown={isPreviewColumnOpen}
          renderStickyColumnSideContent={
            <>
              <ImpactTracker />
              <FaqCard />
            </>
          }
        >
          <RentalsPreviewColumn
            entityId={selectedEntityId}
            isOpen={isPreviewColumnOpen}
            onClosePreviewColumn={handleClosePreviewColumn}
          />
        </ContentSidebar>
      </Row>
    </Container>
  )
})
